import { useCallback, useEffect, useMemo, useState } from "react";
import { getProductCategories } from "../../../services";
import { ProductCategory } from "../../../types/product.type";

const PAGE_SIZE = 100;

/**
 * Loads the product categories a product screen needs: the filter and the drawer
 * need the options, the table and the details page need a code -> name label.
 * Fetched once per screen and shared with the children through props so a screen
 * never asks for the same list twice; `reloadCategories` picks up a category the
 * drawer or the category dialog has just changed.
 */
export function useProductCategories() {
  const [categories, setCategories] = useState<ProductCategory[]>([]);

  const reloadCategories = useCallback(async () => {
    const rows: ProductCategory[] = [];
    let page = 1;
    let total = 0;

    try {
      // The pages run in sequence because the total only arrives with the first
      // response; truncating here would hide categories from the filter and leave
      // the drawer's select blank for the products that use them.
      do {
        const {
          data: { data },
        } = await getProductCategories(page, PAGE_SIZE);
        total = data.count;
        rows.push(...data.rows);
        page += 1;
        if (data.rows.length === 0) break;
      } while (rows.length < total);

      setCategories(rows);
    } catch {
      // The axios interceptor reports the failure. The screen keeps the options it
      // already has rather than dropping to an empty select, and the callers that
      // await a reload are not left with a rejection to handle.
    }
  }, []);

  useEffect(() => {
    reloadCategories();
  }, [reloadCategories]);

  const categoryNameByCode = useMemo(
    () =>
      new Map<string, string>(
        categories.map((category) => [category.code, category.name]),
      ),
    [categories],
  );

  return { categories, categoryNameByCode, reloadCategories };
}
